import type {
  Alert,
  CallOutComeBreakdown,
  DashboardResponse,
  UpcomingCall,
  VolumeTrend,
} from '@/types/dashboard.type';

type DashboardRatePayload = {
  answered_rate?: number;
  error_rate?: number;
  total_calls?: number;
};

type UpcomingCallPayload = {
  scheduler_id: number;
  scheduler_name: string;
  agent_name: string | null;
  scheduled_at: string;
  total_recipients: number;
  status: string;
  recipient_names: string[];
};

type AlertPayload = {
  id: number;
  type: string;
  module: string;
  message: Record<string, unknown>;
  created_at: string;
};

type DashboardPayload = {
  data: {
    answered_rate: DashboardRatePayload;
    error_rate: DashboardRatePayload;
    total_calls: DashboardRatePayload;
    volume_trend: Array<{ date: string; call_volume: number }>;
    call_outcome_breakdown: Array<{ label: string; value: number }>;
    upcoming_calls?: UpcomingCallPayload[];
    recent_alerts?: AlertPayload[];
  };
};

const mapVolumeTrend = (data: { date: string; call_volume: number }): VolumeTrend => {
  return {
    date: new Date(data.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    volume: data.call_volume,
  };
};

const mapCallBreakdown = (data: { label: string; value: number }): CallOutComeBreakdown => {
  return {
    label: data.label,
    value: data.value,
  };
};

const mapUpcomingCall = (data: UpcomingCallPayload): UpcomingCall => {
  return {
    id: String(data.scheduler_id),
    name: data.scheduler_name,
    when: new Date(data.scheduled_at).toLocaleString(),
    agentName: data.agent_name,
    totalRecipients: data.total_recipients,
    status: data.status,
    recipientNames: data.recipient_names ?? [],
  };
};

const mapAlert = (data: AlertPayload): Alert => {
  return {
    id: String(data.id),
    type: data.type,
    module: data.module,
    message: data.message ?? {},
    when: new Date(data.created_at).toLocaleString(),
  };
};

export const mapDashboardResponse = (payload: DashboardPayload): DashboardResponse => {
  const data = payload.data;

  return {
    answered_rate: {
      total: Math.ceil(data.answered_rate.answered_rate),
    },
    error_rate: {
      total: Math.ceil(data.error_rate.error_rate),
    },
    total_calls: {
      total: data.total_calls.total_calls,
    },
    volume_trend: data.volume_trend.map(mapVolumeTrend),
    call_outcome_breakdown: data.call_outcome_breakdown.map(mapCallBreakdown),
    upcoming_calls: (data.upcoming_calls || []).map(mapUpcomingCall),
    alerts: (data.recent_alerts || []).map(mapAlert),
  };
};
